<script lang="ts" setup>
import CorePhoneInput from "@/components/common/CorePhoneInput/CorePhoneInput.vue";
import { useValidation } from "@/composables/validation";
import { ElFormItem, type FormItemRule } from "element-plus";
import { computed, ref, watch } from "vue";
import type { PhoneInputType } from "../CorePhoneInput/types";
import { useI18n } from "vue-i18n";

export interface Props {
  modelValue?: string;
  disabled: boolean;
  required?: boolean;
  id?: string;
}

export interface FieldError {
  field: string;
  fieldValue: string;
  message: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "update:model-value", value: string): void;
}>();

const { validateRequiredField, validatePhone, validateFormItem } =
  useValidation();
const { locale, t } = useI18n();

const input = ref<string>(props.modelValue ?? "");

const phoneValue = ref<PhoneInputType | null>(null);

const formItemRef = ref<InstanceType<typeof ElFormItem> | null>(null);

const rules = computed(() => {
  const result: FormItemRule[] = [validatePhone(phoneValue)];
  if (props.required) result.push(validateRequiredField(input));

  return result;
});

const handleInput = (value: PhoneInputType) => {
  emit("update:model-value", value.userInput);
  phoneValue.value = value;
};

watch(
  () => props.modelValue,
  async (val) => {
    if (input.value === val) return;
    input.value = val ?? "";

    try {
      await formItemRef.value?.validate("change");
    } catch (error) {
      return;
    }
  }
);

watch(locale, () => {
  if (phoneValue.value?.error) {
    phoneValue.value.error = t("validation.invalidPhone");
  }

  const fieldRefs = { formItemRef: formItemRef.value };
  validateFormItem(fieldRefs);
});
</script>

<template>
  <el-form-item
    ref="formItemRef"
    :prop="id"
    :rules="rules"
    :required="required"
  >
    <template #label>
      <slot name="label" />
    </template>
    <CorePhoneInput
      class="phone-input"
      :value="{
        userInput: input,
        formattedNumber: '',
        error: null,
      }"
      :disabled="disabled"
      @input="handleInput($event)"
    />
  </el-form-item>
</template>

<style lang="css" scoped>
.phone-input {
  width: 100%;
}

.is-error .phone-input {
  --so-border-color: var(--so-color-danger);
  --so-color-primary: var(--so-color-danger);
  --so-text-color-secondary: var(--so-color-danger);
}
</style>
